import React, { FunctionComponent, ReactNode, useMemo, useState } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import { PaginationProps } from 'ui/molecules/pagination';
import DataRecordOrdering from 'src/ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum, DataRecordTableCell } from 'src/libraries/data-record-table';
import Translate from 'src/ui/atoms/translate';
import Date from 'src/ui/atoms/date';
import StatusTag from 'src/ui/atoms/status-tag';
import WithDataRecord from 'src/hoc/WithDataRecord';
import {
  AdminApi,
  AdminUserList,
  AdminUsersListOrderingEnum,
  AdminUsersListStatusEnum,
  AdminUsersValuesRetrieveFieldEnum,
} from 'src/api';
import { useDispatch } from 'store/hooks';
import { push } from 'connected-react-router';
import makeLink from 'src/helper/make-link';
import { ISSUER_ROUTES } from '../../pages/routes.config';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import DeleteGroupUserConfirmationModal from '../../pages/group-details/group-users-table/confirm-delete-group-user-modal';
import DeleteGroupUserSuccessModal from '../../pages/group-details/group-users-table/success-delete-group-user-modal';

const getStatusVariant = (status: AdminUsersListStatusEnum) => {
  switch (status) {
    case AdminUsersListStatusEnum.ACTIVE:
      return 'success';
    case AdminUsersListStatusEnum.INVITATION_PENDING:
      return 'warning';
    case AdminUsersListStatusEnum.DEACTIVATED:
    default:
      return 'danger';
  }
};

const getStatusTranslationKey = (status: AdminUsersListStatusEnum) => {
  switch (status) {
    case AdminUsersListStatusEnum.ACTIVE:
      return 'issuerPermissionsUsersTable.active';
    case AdminUsersListStatusEnum.INVITATION_PENDING:
      return 'issuerPermissionsUsersTable.invitationPending';
    case AdminUsersListStatusEnum.DEACTIVATED:
    default:
      return 'issuerPermissionsUsersTable.deactivated';
  }
};

export interface UsersTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  goToDetails: (userId: string) => void;
  users?: AdminUserList[];
  paginationProps?: PaginationProps;
  loading?: boolean;
  hasPermission: boolean;
  showOrganizationColumn?: boolean;
  setFilterPopupHeight?: (height: number) => void;
  setConfirmDeleteGroupUser?: (user: { id: string; email: string }) => void;
  showDeleteButton?: boolean;
  organizationId?: string;
  groupId?: string;
}

export const UsersTable: FunctionComponent<UsersTableProps> = (props) => {
  const {
    className,
    loading,
    hasPermission,
    ordering,
    onOrderBy,
    users,
    paginationProps,
    goToDetails,
    showOrganizationColumn = true,
    setFilterPopupHeight,
    setConfirmDeleteGroupUser,
    showDeleteButton,
    organizationId,
    groupId,
  } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);
  const translate = useTranslateWithStringArgs();

  const userStatusFilterOptions = useMemo(() => {
    return Object.values(AdminUsersListStatusEnum).map((userStatus) => ({
      id: userStatus,
      name: translate(`issuerPermissionsUsersTable.${snakeToCamelCaseWithNumbers(userStatus || '')}`),
    }));
  }, [translate]);

  const cells: DataRecordTableCell<AdminUserList>[] = [
    {
      title: <Translate name="issuerPermissionsUsersTable.email" />,
      render: ({ email }) => email,
      orderingFieldName: AdminUsersListOrderingEnum.EMAIL_ASC,
      type: CellTypeEnum.text,
      filterName: 'email',
      searchFieldName: AdminUsersValuesRetrieveFieldEnum.EMAIL,
      getFilterData: (params: any) =>
        adminApi.adminUsersValuesRetrieve({
          ...params,
          ...(groupId && { groups: [groupId] }),
          ...(organizationId && { organization: [organizationId] }),
        }),
    },
    ...(showOrganizationColumn
      ? [
          {
            title: <Translate name="issuerPermissionsUsersTable.organization" />,
            render: ({ organizationName }: AdminUserList) => organizationName,
            orderingFieldName: AdminUsersListOrderingEnum.ORGANIZATION_NAME_ASC,
            type: CellTypeEnum.text,
            filterName: 'organizationName',
            searchFieldName: AdminUsersValuesRetrieveFieldEnum.ORGANIZATION_NAME,
            getFilterData: (params: any) =>
              adminApi.adminUsersValuesRetrieve({
                ...params,
                ...(groupId && { groups: [groupId] }),
                ...(organizationId && { organization: [organizationId] }),
              }),
          },
        ]
      : []),
    {
      title: <Translate name="issuerPermissionsUsersTable.lastLoginAt" />,
      render: ({ lastLogin }) => lastLogin && <Date showTime>{lastLogin}</Date>,
      orderingFieldName: AdminUsersListOrderingEnum.LAST_LOGIN_ASC,
      type: CellTypeEnum.date,
      filterName: 'lastLogin',
    },
    {
      title: <Translate name="issuerPermissionsUsersTable.status" />,
      render: ({ status }) => (
        <StatusTag variant={getStatusVariant(status)} truncate={true}>
          <Translate name={getStatusTranslationKey(status)} />
        </StatusTag>
      ),
      orderingFieldName: AdminUsersListOrderingEnum.STATUS_ASC,
      width: 192,
      type: CellTypeEnum.text,
      filterName: 'status',
      getFilterData: () => {
        return userStatusFilterOptions;
      },
    },
    ...(showDeleteButton && setConfirmDeleteGroupUser
      ? [
          {
            title: <Translate name="issuerGroupUsersTable.actions" />,
            render: ({ id, email }: { id: string; email: string }) => (
              <ActionButtons align="center" spacingTop="none">
                <Button variant="secondary" size="xsmall" onClick={() => setConfirmDeleteGroupUser({ id, email })}>
                  <Translate name="issuerGroupUsersTable.deleteButton" />
                </Button>
              </ActionButtons>
            ),
            width: 194,
          },
        ]
      : []),
  ];

  return (
    <div className={cx('users-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={users}
        paginationProps={paginationProps}
        loading={loading}
        hasPermission={hasPermission}
        onGoToDetails={goToDetails}
        cells={cells}
        setFilterPopupHeight={setFilterPopupHeight}
      />
    </div>
  );
};

export interface UsersTableWrapperProps {
  status?: AdminUsersListStatusEnum[];
  title?: ReactNode;
  forceDataLoad?: boolean;
  setForceDataLoad?: (val: boolean) => void;
  skipFilterUrlSync?: boolean;
  hasPermission: boolean;
  organizationId?: string;
  showOrganizationColumn?: boolean;
  setFilterPopupHeight?: (height: number) => void;
  showOrganizationFilter?: boolean;
  groupId?: string;
  groupName?: string;
  showDeleteButton?: boolean;
}

export const UsersTableWrapper: FunctionComponent<UsersTableWrapperProps> = WithDataRecord<AdminApi, AdminUserList>(
  AdminApi,
)(
  ({
    data,
    ordering,
    onOrderBy,
    loading,
    hasPermission,
    paginationProps,
    showOrganizationColumn,
    setFilterPopupHeight,
    setForceDataLoad,
    showDeleteButton,
    groupName = '',
    groupId = '',
    organizationId,
  }) => {
    const dispatch = useDispatch();

    const [confirmDeleteGroupUser, setConfirmDeleteGroupUser] = useState<{ id: string; email: string } | null>(null);
    const [isSuccessDeleteGroupUserModalOpen, setSuccessDeleteGroupUserModalOpen] = useState(false);

    const goTo = (userId: string) => {
      dispatch(push(makeLink(ISSUER_ROUTES.userDetails, { userId })));
    };

    return (
      <>
        <UsersTable
          onOrderBy={onOrderBy}
          ordering={ordering}
          users={data}
          loading={loading}
          hasPermission={hasPermission}
          paginationProps={paginationProps}
          goToDetails={(userId: string) => goTo(userId)}
          showOrganizationColumn={showOrganizationColumn}
          setFilterPopupHeight={setFilterPopupHeight}
          setConfirmDeleteGroupUser={setConfirmDeleteGroupUser}
          showDeleteButton={showDeleteButton}
          organizationId={organizationId}
          groupId={groupId}
        />
        {!!confirmDeleteGroupUser && (
          <DeleteGroupUserConfirmationModal
            groupName={groupName}
            groupId={groupId}
            userId={confirmDeleteGroupUser.id}
            email={confirmDeleteGroupUser.email}
            onClose={() => setConfirmDeleteGroupUser(null)}
            onDelete={() => {
              setSuccessDeleteGroupUserModalOpen(true);
              setConfirmDeleteGroupUser(null);
            }}
          />
        )}
        {isSuccessDeleteGroupUserModalOpen && setForceDataLoad && (
          <DeleteGroupUserSuccessModal
            onClose={() => {
              setForceDataLoad?.(true);
              setSuccessDeleteGroupUserModalOpen(false);
            }}
          />
        )}
      </>
    );
  },
  (api, props, offset, limit, ordering) => {
    const { groupId, organizationId, ...remainingProps } = props;

    return api.adminUsersList({
      offset,
      limit,
      ordering: ordering as AdminUsersListOrderingEnum,
      ...(groupId && { groups: [groupId] }),
      ...(organizationId && { organization: [organizationId] }),
      ...remainingProps,
    });
  },
);

export default UsersTableWrapper;
